"use client";

import { DirectusFile } from "@directus/sdk";
// Assuming these imports point to your components
import Typography from "../common/typography";
import { FiveStar } from "../icons/stars/fiveStar";
import Image from "next/image";
import Link from "next/link";
// Import useState for managing expansion state
import { useState } from "react";
import { formatTimestampToNaturalLanguage } from "@/lib/utils";

interface TypeOfReviewCardProps {
  className?: string;
  reviewerName?: string;
  reviewerProfilePicture?: DirectusFile;
  reviewDescription?: string;
  reviewImages?: DirectusFile[];
  reviewUrl?: string;
  reviewDateTimeStamp?: string;
}

export const TypeOfReviewCard = (props: TypeOfReviewCardProps) => {
  // State to track if the review description is expanded
  const [isExpanded, setIsExpanded] = useState(false);

  // Toggle function for the "More"/"Less" button
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Use the provided description or a default, long enough for demonstration
  const descriptionContent =
    props.reviewDescription ||
    "This is a placeholder review description that is definitely long enough to span more than four lines. We need to ensure that when the card is first loaded, the text is gracefully truncated using the line-clamp utility. This provides a cleaner look, saving vertical space, and giving the user the option to read the rest if they find the snippet interesting. When 'More' is clicked, the full text will be revealed.";

  // Simple heuristic to decide if the "More" button should even be shown.
  // We assume any description over ~200 characters will likely be more than 4 lines.
  const isDescriptionLong = descriptionContent.length > 200;

  //convert datetime stamp to natural language
  const datetime = props.reviewDateTimeStamp
    ? formatTimestampToNaturalLanguage(props.reviewDateTimeStamp)
    : undefined;

  return (
    <article className="lg:p-30px h-full pt-30px pb-5 px-5 flex flex-col shadow-default lg:max-w-630px lg:min-w-630px w-full rounded-14">
      <header className="flex flex-col gap-y-15px">
        <div className="flex gap-x-15px items-center">
          {props.reviewerProfilePicture && (
            <figure>
              <Image
                width={45}
                height={45}
                src={props.reviewerProfilePicture.filename_disk!}
                alt="Reviewer photo"
                className="rounded-full"
              />
            </figure>
          )}
          {!props.reviewerProfilePicture && (
            <div className="w-[45px] h-[45px] flex items-center justify-center shrink-0 aspect-square bg-[#c4f2ff] rounded-full">
              {props.reviewerName?.substring(0, 2)}
            </div>
          )}
          <Typography size="body1" weight={500}>
            {props.reviewerName || "Robert H."}
          </Typography>
        </div>

        {/* review rating */}
        <span className="flex flex-row items-center">
          <FiveStar />

          {datetime && (
            <Typography
              size="unresponsiveBody2"
              color="darkGray"
              className="ml-15px mt-[4px]"
            >
              {datetime}
            </Typography>
          )}
        </span>
      </header>

      <section className="mt-15px lg:flex lg:flex-row flex-col lg:items-end">
        <Typography
          size="body1"
          // Conditionally apply 'line-clamp-4' when not expanded
          className={isExpanded ? "" : "line-clamp-4"}
        >
          {descriptionContent}
        </Typography>

        {/* Render the toggle button only if the description is long */}
        {isDescriptionLong && (
          <button
            onClick={toggleExpanded}
            className="text-sm font-medium text-primary hover:underline mt-1 focus:outline-none"
            aria-expanded={isExpanded}
          >
            {isExpanded ? "Less" : "More"}
          </button>
        )}
      </section>

      {/* images */}
      {props.reviewImages && (
        <section className="flex flex-row gap-x-5px mt-15px overflow-x-scroll no-scrollbar">
          {props.reviewImages.map((image, idx) => (
            <Image
              src={
                image?.filename_disk ||
                "ff908faa-4fbc-4226-bed6-a8c38fe86a49.jpg"
              }
              alt={image?.description || "TESTING"}
              key={idx}
              width={106}
              height={106}
              className="object-cover aspect-square rounded-xl flex-shrink-0"
            />
          ))}
        </section>
      )}

      {/* footer */}
      <footer className="flex gap-x-2 justify-end items-center  mt-auto">
        <Typography size="body2" color="copyGray">
          Posted on&nbsp;
          {props.reviewUrl ? (
            <Link
              href={props.reviewUrl || "/"}
              target="_blank"
              className="underline"
            >
              Google:
            </Link>
          ) : (
            "Google:"
          )}
        </Typography>

        <Image
          src={"c9b2967a-4405-43a5-9eff-4a410453788e.svg"}
          alt=""
          aria-hidden
          height={24}
          width={24}
          className="object-cover"
        />
      </footer>
    </article>
  );
};
